import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, Subject, ReplaySubject, fromEvent } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { APP_CONFIG } from '@/environments/environment';
import { TokenStorageService } from './token-storage.service';
import { Notification } from '@/app/shared/interfaces/notification.interface';

/**
 * WebSocket service for real-time notifications
 * Connects to backend Socket.IO gateway for live notification updates
 */
@Injectable({ providedIn: 'root' })
export class NotificationWebSocketService implements OnDestroy {
    private tokenStorage = inject(TokenStorageService);
    private socket: Socket | null = null;
    private destroy$ = new Subject<void>();

    // Connection state
    private connectedSignal = signal<boolean>(false);
    readonly connected = this.connectedSignal.asReadonly();

    // Socket.IO event subjects
    private notificationReceived$ = new Subject<Notification>();
    private notificationUpdated$ = new Subject<Notification>();
    private notificationDeleted$ = new Subject<string>();
    private unreadCountUpdated$ = new Subject<number>();
    private statsUpdated$ = new Subject<any>();
    private error$ = new Subject<string>();

    /**
     * Observable streams for notification events
     */
    readonly onNotificationReceived = this.notificationReceived$.asObservable();
    readonly onNotificationUpdated = this.notificationUpdated$.asObservable();
    readonly onNotificationDeleted = this.notificationDeleted$.asObservable();
    readonly onUnreadCountUpdated = this.unreadCountUpdated$.asObservable();
    readonly onStatsUpdated = this.statsUpdated$.asObservable();
    readonly onError = this.error$.asObservable();

    constructor() {
        // Auto-connect when token is available
        this.tokenStorage.tokens$.pipe(takeUntil(this.destroy$)).subscribe(({ accessToken }: { accessToken: string | null }) => {
            console.log('[NotificationWebSocket] Token changed:', accessToken ? 'Token present' : 'No token');

            if (accessToken && !this.socket) {
                console.log('[NotificationWebSocket] Valid token found, initiating connection');
                this.connect(accessToken);
            } else if (accessToken && this.socket && !this.socket.connected) {
                console.log('[NotificationWebSocket] Reconnecting with new token');
                this.disconnect();
                this.connect(accessToken);
            } else if (!accessToken && this.socket) {
                console.log('[NotificationWebSocket] No token, disconnecting');
                this.disconnect();
            }
        });
    }

    /**
     * Connect to WebSocket server
     * Sends token via both auth object and Authorization header (same as REST API)
     */
    private connect(token: string): void {
        if (this.socket?.connected) {
            console.log('[NotificationWebSocket] Already connected');
            return;
        }

        // Parse backend URL to get the correct WebSocket URL
        const backendUrl = APP_CONFIG.apiUrl.replace('/api/v1', '');

        console.log('[NotificationWebSocket] Connecting to:', backendUrl, 'with path: /notifications/socket');

        this.socket = io(backendUrl, {
            path: '/notifications/socket',
            // Send token via auth object (Socket.IO v4+ preferred method)
            auth: {
                token,
            },
            // Also send via Authorization header (consistent with REST API)
            extraHeaders: {
                Authorization: `Bearer ${token}`,
            },
            transports: ['websocket', 'polling'],
            autoConnect: false,
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
        });

        // Setup listeners before connecting
        this.setupEventListeners();

        // Connect the socket
        this.socket.connect();
    }

    /**
     * Setup Socket.IO event listeners
     */
    private setupEventListeners(): void {
        if (!this.socket) return;

        // Connection events
        this.socket.on('connect', () => {
            console.log('[NotificationWebSocket] Connected');
            this.connectedSignal.set(true);
        });

        this.socket.on('disconnect', (reason: string) => {
            console.log('[NotificationWebSocket] Disconnected:', reason);
            this.connectedSignal.set(false);
        });

        this.socket.on('connect_error', (error: Error) => {
            console.error('[NotificationWebSocket] Connection error:', error);
            this.error$.next(error.message);
        });

        // Notification events
        this.socket.on('notification', (notification: Notification) => {
            console.log('[NotificationWebSocket] New notification received:', notification);
            this.notificationReceived$.next(notification);
        });

        this.socket.on('notification:updated', (notification: Notification) => {
            console.log('[NotificationWebSocket] Notification updated:', notification);
            this.notificationUpdated$.next(notification);
        });

        this.socket.on('notification:deleted', (notificationId: string) => {
            console.log('[NotificationWebSocket] Notification deleted:', notificationId);
            this.notificationDeleted$.next(notificationId);
        });

        // Unread count event (sent on connection and after changes)
        this.socket.on('unread_count', (data: { count: number }) => {
            console.log('[NotificationWebSocket] Unread count updated:', data.count);
            this.unreadCountUpdated$.next(data.count);
        });

        // Notification read event (includes updated unread count)
        this.socket.on('notification:read', (data: { notificationId: string; unreadCount: number }) => {
            console.log('[NotificationWebSocket] Notification marked as read:', data);
            this.unreadCountUpdated$.next(data.unreadCount);
        });

        // All notifications marked as read
        this.socket.on('notifications:all_read', (data: { unreadCount: number }) => {
            console.log('[NotificationWebSocket] All notifications marked as read');
            this.unreadCountUpdated$.next(data.unreadCount);
        });

        this.socket.on('notification:stats', (stats: any) => {
            console.log('[NotificationWebSocket] Stats updated:', stats);
            this.statsUpdated$.next(stats);
        });

        // Error events
        this.socket.on('error', (error: any) => {
            console.error('[NotificationWebSocket] Error:', error);
            this.error$.next(error.message || 'Unknown error');
        });

        this.socket.on('exception', (exception: any) => {
            console.error('[NotificationWebSocket] Exception:', exception);
            this.error$.next(exception.message || 'Unknown exception');
        });
    }

    /**
     * Disconnect from WebSocket server
     */
    disconnect(): void {
        if (this.socket) {
            console.log('[NotificationWebSocket] Disconnecting...');
            this.socket.disconnect();
            this.socket = null;
            this.connectedSignal.set(false);
        }
    }

    /**
     * Manually reconnect to WebSocket server
     */
    reconnect(): void {
        const token = this.tokenStorage.getAccessToken();
        if (token) {
            this.disconnect();
            this.connect(token);
        }
    }

    /**
     * Request unread count from server
     */
    requestUnreadCount(): void {
        if (this.socket?.connected) {
            console.log('[NotificationWebSocket] Requesting unread count from server...');
            this.socket.emit('get_unread_count');
        } else {
            console.warn('[NotificationWebSocket] Cannot request unread count, not connected');
        }
    }

    /**
     * Emit a custom event to the server
     */
    emit(event: string, data?: any): void {
        if (this.socket?.connected) {
            this.socket.emit(event, data);
        } else {
            console.warn('[NotificationWebSocket] Cannot emit, not connected');
        }
    }

    /**
     * Listen for a custom event from the server
     */
    on<T = any>(event: string): Observable<T> {
        if (!this.socket) {
            return new Observable((observer) => {
                observer.error('Socket not initialized');
            });
        }
        return fromEvent(this.socket, event).pipe(
            takeUntil(this.destroy$),
            map((data) => data as T)
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.disconnect();
    }
}

